import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Download, Check } from '@mui/icons-material';
import { filesAPI } from '../../services/api';

const FORMATS = {
  'tab': {
    label: 'Nome + link (tab)',
    render: (files) => files.map((f) => `${f.filename}\t${f.url}`).join('\n'),
    hint: 'Incolla in Word o Excel: nome e link su due colonne.',
  },
  'url': {
    label: 'Solo link',
    render: (files) => files.map((f) => f.url).join('\n'),
    hint: 'Una URL per riga.',
  },
  'named': {
    label: 'Nome — link',
    render: (files) => files.map((f) => `${f.filename} — ${f.url}`).join('\n'),
    hint: 'Testo piatto leggibile.',
  },
};

export default function ExportLinksDialog({ open, onClose, params, title = 'Esporta link file' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [format, setFormat] = useState('tab');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !params) {
      setData(null);
      setError('');
      setCopied(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    filesAPI
      .exportLinks(params)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Errore durante il recupero dei link');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, params]);

  const text = useMemo(() => {
    if (!data?.files?.length) return '';
    return FORMATS[format].render(data.files);
  }, [data, format]);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `link-file-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Recupero link in corso...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && data && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {data.count === 0
                  ? 'Nessun file trovato.'
                  : `${data.count} file trovati.`}
              </Typography>

              {data.count > 0 && (
                <>
                  <Box sx={{ flex: 1 }} />
                  <ToggleButtonGroup
                    size="small"
                    value={format}
                    exclusive
                    onChange={(_, v) => v && setFormat(v)}
                  >
                    {Object.entries(FORMATS).map(([k, v]) => (
                      <ToggleButton
                        key={k}
                        value={k}
                        sx={{ textTransform: 'none', py: 0.3, px: 1 }}
                      >
                        {v.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </>
              )}
            </Box>

            {data.count > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {FORMATS[format].hint}
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={12}
                  value={text}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: 13 },
                  }}
                  onFocus={(e) => e.target.select()}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Chiudi
        </Button>
        {data?.count > 0 && (
          <>
            <Tooltip title="Scarica come file .txt">
              <Button
                startIcon={<Download fontSize="small" />}
                onClick={handleDownload}
                sx={{ textTransform: 'none', boxShadow: 'none' }}
              >
                Scarica .txt
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
              onClick={handleCopy}
              color={copied ? 'success' : 'primary'}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              {copied ? 'Copiato' : 'Copia tutto'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
